import { PageFrame } from '@/components/layout/PageFrame';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { PANEL_CONTAINER } from '@/lib/ui-tokens';

const CHIP_LABELS = ['All', 'News', 'Community', 'Brands I follow', 'Competitors', 'Trends'];

interface DiscoverPageSkeletonProps {
  /**
   * When true, renders the full page chrome (header greeting + chips bar +
   * right-rail sidebar). When false, renders only the inner feed (used by
   * DiscoverPage when it has chips/header already mounted but the feed query
   * is still pending).
   */
  withChrome?: boolean;
}

export function DiscoverPageSkeleton({
  withChrome = true,
}: DiscoverPageSkeletonProps): JSX.Element {
  if (!withChrome) {
    return <DiscoverFeedSkeleton />;
  }

  return (
    // This is the Suspense fallback for `/discover` (routes/index.tsx), so it
    // is the first thing rendered on the route. It hand-rolled the page column
    // — same measure, but a flat `px-6` against `PageFrame`'s `px-4 sm:px-6`,
    // so the skeleton sat 8px further in than the page it stands in for and
    // every card jumped left the moment real content arrived. A layout-matched
    // skeleton has to take the layout from the same place the layout does.
    <PageFrame className="pb-7 pt-4">
      <section className="mb-6 space-y-4">
        <Skeleton className="h-9 w-72 rounded-md" />
        <div className="grid gap-2 md:grid-cols-3">
          {[0, 1, 2].map((n) => (
            <div
              key={n}
              className="flex flex-col gap-2 rounded-xl border border-border/60 bg-popover/95 px-3.5 py-3 shadow-elevation-1"
            >
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ))}
        </div>
      </section>

      <div className="mb-4 border-b border-border/40 pb-3">
        <div className="flex items-center justify-between gap-4">
          <nav
            aria-hidden
            className="no-scrollbar flex min-w-0 items-center gap-1.5 overflow-x-auto md:flex-wrap"
          >
            {CHIP_LABELS.map((label) => (
              <Skeleton
                key={label}
                className="h-7 shrink-0 rounded-full"
                style={{ width: `${Math.max(48, label.length * 8 + 24)}px` }}
              />
            ))}
          </nav>
          <Skeleton className="hidden h-3 w-40 md:block" />
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <DiscoverFeedSkeleton />
        </div>
        <div className="hidden lg:block">
          <DiscoverSidebarSkeleton />
        </div>
      </div>
    </PageFrame>
  );
}

function DiscoverFeedSkeleton(): JSX.Element {
  return (
    <div className="space-y-7">
      <div className="flex flex-col gap-5 sm:flex-row-reverse sm:items-start sm:gap-7">
        <Skeleton className="aspect-[16/11] w-full rounded-xl sm:w-[380px]" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-8 w-5/6" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <div className="flex items-center gap-3 pt-2">
            <Skeleton className="size-5 rounded-full" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      </div>
      <Separator className="bg-border/40" />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {[0, 1, 2].map((n) => (
          <div key={n} className="space-y-2">
            <Skeleton className="aspect-[16/9] w-full rounded-lg" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ))}
      </div>
      <Separator className="bg-border/40" />
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-7">
        <Skeleton className="aspect-[16/11] w-full rounded-xl sm:w-[260px]" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-6 w-5/6" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
      <Separator className="bg-border/40" />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {[0, 1, 2].map((n) => (
          <div key={n} className="space-y-2">
            <Skeleton className="aspect-[16/9] w-full rounded-lg" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ))}
      </div>
    </div>
  );
}

function DiscoverSidebarSkeleton(): JSX.Element {
  return (
    <aside className="flex w-full flex-col gap-3">
      <div className="rounded-xl border border-border/60 bg-card/40 p-5">
        <div className="space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-9 w-32" />
          <Skeleton className="h-3 w-28" />
        </div>
      </div>
      <div className="rounded-xl border border-border/60 bg-card/40 p-5">
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((n) => (
            <div key={n} className="space-y-1">
              <Skeleton className="size-4 rounded-full" />
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-2 w-10" />
            </div>
          ))}
        </div>
      </div>
      <div className={PANEL_CONTAINER}>
        <div className="space-y-2 p-5">
          <Skeleton className="h-3 w-24" />
          <div className="flex flex-wrap gap-1.5 pt-1">
            {[0, 1, 2, 3].map((n) => (
              <Skeleton key={n} className="h-6 w-16 rounded-full" />
            ))}
          </div>
        </div>
        <div className="space-y-2 p-5">
          <Skeleton className="h-3 w-28" />
          <div className="flex flex-wrap gap-1.5 pt-1">
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <Skeleton key={n} className="h-6 w-16 rounded-full" />
            ))}
          </div>
        </div>
      </div>
      <div className={PANEL_CONTAINER}>
        <div className="space-y-3 p-5">
          <Skeleton className="h-3 w-32" />
          {[0, 1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-3">
              <Skeleton className="size-10 rounded-md" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-3 w-4/5" />
                <Skeleton className="h-2 w-1/3" />
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-3 p-5">
          <Skeleton className="h-3 w-28" />
          {[0, 1, 2].map((n) => (
            <div key={n} className="flex items-center gap-2">
              <Skeleton className="size-3" />
              <Skeleton className="h-3 flex-1" />
              <Skeleton className="h-3 w-8" />
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}

export default DiscoverPageSkeleton;
